// The repaintable palette, admin-editable at /config (analisis_config type
// 'tema'). THE one place a colour is resolved — screen, PDF and Excel all read
// from here, so a config change lands on all three at once.
//
// Three knobs only. The shades that hang off them (the darker table sub-header
// row, the darker total-row border) are DERIVED by darkening, so there is no
// second value to keep in step. Status colours (amber "warning", red "no
// response") stay hardcoded in the pages — they encode meaning, not branding.
import Vue from "vue";
import { exportMeta, clearExportMeta } from "@/services/surveyYear";

// Mirrors THEME_DEFAULTS in the backend's analisis_config/config.js. Used until
// the fetch lands, and whenever it fails — so the UI is never unpainted.
const DEFAULTS = { graf: "#0EA5A4", kepalaJadual: "#1B7E91", tajukLaporan: "#1B7E91" };

// Observable so templates binding T.graf repaint when the config arrives.
export const T = Vue.observable({ ...DEFAULTS });

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/* ---- conversions to the three places a colour is consumed ---- */

// "#0EA5A4" -> [14, 165, 164]  (jsPDF)
export function rgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

// "#0EA5A4" -> "FF0EA5A4"  (ExcelJS ARGB)
export const argb = hex => "FF" + hex.slice(1).toUpperCase();

// Scale a colour's channels: -0.23 darkens by 23%, +0.2 lightens toward white.
// How every derived shade is produced, so only the base colour is configurable.
export function shade(hex, amount) {
  const [r, g, b] = rgb(hex);
  const f = c => amount < 0
    ? Math.round(c * (1 + amount))
    : Math.round(c + (255 - c) * amount);
  return "#" + [f(r), f(g), f(b)].map(c => c.toString(16).padStart(2, "0")).join("").toUpperCase();
}

// The darker second header row / total-row border.
//
// -0.236 is calibrated to reproduce the shipped #15606F from #1B7E91 exactly,
// so seeding the config repaints nothing. The two original darks were picked by
// hand and are NOT one factor apart, so the other one lands 4/255 off
// (#0B7E7D vs #0B827F) — it is a 1px total-row border, invisible at that delta.
export const dark = hex => shade(hex, -0.236);

/* ---- loading ---- */

let loaded = null;

// Resolve the palette, publish it to T and to the CSS custom properties, and
// cache the promise. Safe to call from anywhere, any number of times: exports
// await it so their colours are never stale, App.vue kicks it off at boot so
// the screen is painted before the first table renders.
export function loadTheme() {
  if (!loaded) {
    loaded = exportMeta()
      .then(meta => {
        const t = meta.theme || {};
        for (const k of Object.keys(DEFAULTS)) {
          if (HEX_RE.test(t[k] || "")) T[k] = t[k].toUpperCase();
        }
        return T;
      })
      .catch(() => T);        // keep the defaults; never block an export
    loaded = loaded.then(t => { applyCss(); return t; });
  }
  return loaded;
}

// Re-read the palette from the server, bypassing both caches. Called after an
// admin saves a colour so the change is visible immediately — T is observable
// and the CSS custom properties are rewritten, so screens repaint without a
// browser reload.
export function reloadTheme() {
  clearExportMeta();
  loaded = null;
  return loadTheme();
}

// Publish to :root so scoped CSS can use var(--warna-*) instead of a literal.
// Every var has the shipped default as its fallback, so a page still paints
// correctly if this never runs (SSR-less first paint, failed fetch, tests).
function applyCss() {
  const s = document.documentElement.style;
  s.setProperty("--warna-graf", T.graf);
  s.setProperty("--warna-graf-gelap", dark(T.graf));
  s.setProperty("--warna-kepala-jadual", T.kepalaJadual);
  s.setProperty("--warna-kepala-jadual-2", dark(T.kepalaJadual));
  s.setProperty("--warna-tajuk-laporan", T.tajukLaporan);
}
